import { Box, Divider, Grid, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatDuration, formatPriceStart, formatTimeDuration } from 'utils/format';
import { useTranslation } from 'i18n';
import { useEmbeddable } from 'hooks';
import { makeStyles } from 'tss-react/mui';
import { getSessionsGroups, isSessionEndTimeHidden } from 'utils/transform';
import { routes } from 'utils/routing';
import Image from '../Image';
import OrgsBox from '../EventCard/OrgsBox';
import Button from '../Button';

export interface IProps {
  anchorPosition: { top: number; left: number } | null;
  handleClosePopover: () => void;
  selectedEvent: any;
}

const useStyles = makeStyles()((theme) => ({
  floating: {
    position: 'absolute',
    zIndex: 999999,
    width: 256,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  imageContainer: {
    position: 'relative',
  },
  orgWrapper: {
    backgroundColor: 'white',
    width: 'fit-content',
    padding: '16px 8px 24px 12px',
  },
  orgTitleWrapper: {
    paddingTop: '0px !important',
    paddingLeft: '16px !important',
  },
  orgImageContainer: {
    position: 'relative',
    height: 35,
    width: 35,
    paddingTop: '0px !important',
    paddingLeft: '0px !important',
    zIndex: 100,
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        width: 104,
        overflow: 'hidden',
        '& img': {
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
        },
      },
    },
  },
  button: {
    borderRadius: 100,
    minWidth: 115,
    height: 32,
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&.disabled': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
}));

const EventInfoView: React.FC<IProps> = ({ anchorPosition, handleClosePopover, selectedEvent }) => {
  const { classes, cx } = useStyles();
  const [t] = useTranslation('public');
  const isEmbedded = useEmbeddable();
  // In embed mode, following the "get tickets" link should open a new tab rather than navigating
  // the partner site's iframe away from the calendar (JPA-8032).
  const newTabLinkProps = isEmbedded ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  const getSelectedEventTimeLabel = () => {
    if (!selectedEvent) return null;

    const { start_at, end_at, sessions, timezone } = selectedEvent;

    const hasMultipleSessions = Array.isArray(sessions) && sessions.length > 1;

    if (hasMultipleSessions) {
      const groups = getSessionsGroups(sessions, timezone);
      return groups.length > 1 ? t('common:general.multipleDays') : t('common:general.multipleTimes');
    }

    const formatted = formatTimeDuration(start_at, isSessionEndTimeHidden(sessions, end_at) ? null : end_at, timezone);
    if (!formatted) return null;

    const [time, suffix] = formatted.split('|');

    return (
      <>
        {time}
        <span>{suffix}</span>
      </>
    );
  };

  const getEventLink = (eventId: number) => routes.event(eventId).link;

  const closeRef = useRef(handleClosePopover);
  closeRef.current = handleClosePopover;

  useEffect(() => {
    if (!anchorPosition) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('#event-floating-panel')) return;
      closeRef.current();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorPosition]);

  if (!anchorPosition || !selectedEvent) return null;

  // Remove the automatic adjustment - use the exact position calculated in useEventPopover
  const adjustedTop = anchorPosition.top;
  const adjustedLeft = anchorPosition.left;

  // console.log('EventInfoView positioning:', {
  //   originalAnchorPosition: anchorPosition,
  //   adjustedTop,
  //   adjustedLeft,
  //   'top in viewport': adjustedTop - (window.pageYOffset || document.documentElement.scrollTop),
  //   'viewport height': window.innerHeight,
  // });

  const priceLabel = formatPriceStart(
    selectedEvent.min_ticket_price || 0,
    selectedEvent.max_ticket_price || 0,
    selectedEvent.default_currency,
    true,
    true,
  );

  const effectivePriceLabel = selectedEvent.is_hidden_price ? '' : priceLabel;
  const isFree = String(priceLabel).trim().toUpperCase() === 'FREE';

  return createPortal(
    <Box
      id="event-floating-panel"
      className={classes.floating}
      style={{
        top: adjustedTop,
        left: adjustedLeft,
      }}
    >
      <Grid container justifyContent="space-between" spacing={3}>
        <Grid item xs={12}>
          <div className={classes.imageContainer}>
            <Image
              variant="rectangle"
              rounded
              src={selectedEvent.main_image || selectedEvent.images?.find((img: any) => img.main)?.image?.original_url}
              alt={selectedEvent.title}
              priority
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1} style={{ paddingLeft: '24px', paddingRight: '24px' }}>
            <Grid item xs={12}>
              {selectedEvent.start_at && selectedEvent.timezone && (
                <Grid container spacing={0}>
                  {!selectedEvent.hide_date && (
                    <Grid item sx={{ marginRight: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDuration(selectedEvent.start_at, selectedEvent.end_at, selectedEvent.timezone)}
                      </Typography>
                    </Grid>
                  )}

                  {!selectedEvent.hide_time && (
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight={500}>
                        {getSelectedEventTimeLabel()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" fontWeight={600}>
                {selectedEvent.title}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" component="span">
                <Grid className={classes.orgWrapper} container spacing={1.5}>
                  <Grid item className={classes.orgImageContainer}>
                    <Image
                      variant="square"
                      objectFit="cover"
                      src={selectedEvent.organization.logo || '/static/img/defaultOrgLogo.png'}
                      alt={selectedEvent.organization.title}
                    />
                  </Grid>

                  <Grid item xs className={classes.orgTitleWrapper}>
                    <Typography variant="caption" style={{ fontWeight: 500 }}>
                      {t('common:general.hostedBy')}
                    </Typography>

                    <Typography variant="caption" color="primary" style={{ fontWeight: 500 }}>
                      <OrgsBox
                        eventId={selectedEvent.id}
                        orgs={[
                          {
                            id: selectedEvent.organization.id,
                            title: selectedEvent.organization.title,
                          },
                          ...selectedEvent.organization_mentions.map((org: any) => ({
                            id: org.organization.id,
                            title: org.organization.title,
                          })),
                        ]}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} style={{ paddingTop: 0 }}>
          <Divider style={{ borderColor: '#CAD1DE' }} />
        </Grid>

        <Grid item xs={12} style={{ padding: '24px', paddingLeft: '48px' }}>
          {isFree ? (
            <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle2">{effectivePriceLabel}</Typography>
              </Grid>
              {selectedEvent?.settings?.is_public && (
                <Grid item>
                  <Button
                    className={cx(classes.button)}
                    variant="contained"
                    disabled={selectedEvent.is_draft}
                    href={getEventLink(selectedEvent.id)}
                    {...newTabLinkProps}
                  >
                    {selectedEvent.cta_button_label_display || 'get tickets'}
                  </Button>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container justifyContent="flex-end">
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: 'fit-content',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    textAlign: 'center',
                    marginBottom: 0.5,
                    lineHeight: 1.2,
                  }}
                >
                  {effectivePriceLabel}
                </Typography>

                {selectedEvent?.settings?.is_public && (
                  <Button
                    className={cx(classes.button)}
                    variant="contained"
                    disabled={selectedEvent.is_draft}
                    href={getEventLink(selectedEvent.id)}
                    {...newTabLinkProps}
                  >
                    {selectedEvent.cta_button_label_display || 'get tickets'}
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>,
    document.body,
  );
};

export default EventInfoView;
